import { CompaniesListResponse } from '@/api/company'
import { DocumentCategory, Filters } from '@/api/documents'
import {
  BackComponent,
  Column,
  PageMainContent,
  Row,
  Typography,
} from '@/components'
import { useDebouncedCallback } from '@/utils'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useOutletContext } from 'react-router-dom'
import { ActionBar } from './ActionBar'
import DocumentsPageError from './Error'
import { SkeletonLoader } from './Skeleton'
import { Container } from './styles'
import DocumentsTable from './Table'
import { useCompaniesDocuments } from './useCompaniesDocuments'
import { ThemeColor } from '@/enums'

export const DocumentsPage = () => {
  const { t } = useTranslation()
  const [search, setSearch] = useState('')
  const [filters, setFilters] = useState<Filters>({
    documentCategories: [] as DocumentCategory[],
    insuranceCategories: [] as string[],
    date: { from: undefined, to: undefined },
  })

  const debouncedSearch = useDebouncedCallback((input: string) => {
    setSearch(input)
  }, 200)

  const { companies } = useOutletContext<{ companies: CompaniesListResponse }>()

  const { documentsMap, isLoading, isError } = useCompaniesDocuments(
    search,
    filters,
    companies,
  )

  const noDocumentsFound = Object.values(documentsMap).every(
    (locations) => locations.length === 0,
  )

  return (
    <PageMainContent>
      <Container>
        <Column gap="8px">
          <BackComponent noContentGutter noMargin />
          <Typography bold variant="h3">
            {t('documentsPage.title')}
          </Typography>
        </Column>
        <ActionBar setSearch={debouncedSearch} applyFilters={setFilters} />

        {isLoading && <SkeletonLoader />}

        {isError && <DocumentsPageError />}

        {noDocumentsFound && !isLoading && !isError && (
          <Container>
            <Typography color={ThemeColor.glass500} variant="p1Body">
              {t('documentsPage.noDocumentsFound')}
            </Typography>
          </Container>
        )}

        {documentsMap &&
          Object.entries(documentsMap).map(([company, locations], index) => {
            const [companyId, companyName] = company.split('/')

            return (
              locations.length > 0 && (
                <Container key={index}>
                  <Row marginTop="18px">
                    <Typography bold variant="h4">
                      {companyName}
                    </Typography>
                  </Row>
                  {locations.map((location) => (
                    <DocumentsTable
                      companyId={companyId}
                      key={location.locationName}
                      location={location}
                    />
                  ))}
                </Container>
              )
            )
          })}
      </Container>
    </PageMainContent>
  )
}
